<script setup>
import { WdsBreadCrumbs } from '@wds/components';
import { SEGMENT_PAGE_DATA } from '@/config/analytics';
import { APPDOWNLOADBANNERLINKS } from '@/config/app-download-banner-links.js';
import { AREAS_TYPES } from '@/config/areas-types.js';

// ### MIDDLEWARE TO VALIDATE ROUTE
definePageMeta({ middleware: 'validate-country' });

const { $tracking } = useNuxtApp();

const {
  t,
  locale,
} = useI18n();
const { useUrl } = useUrls();
const { isExtraSmallScreen } = useMq();
const route = useRoute();
const { buildSeo } = generateSeo();
const { useRouteManager } = useRoutes();
const { useImage } = useImages();
const { useFormatter } = useFormatters();

// ### COMPOSABLES - APIs
const {
  getAllRegionsByCountryId,
  getAllAreasByCountryId,
  getAverageCityPricesByCountryName,
} = useApiCountries();
const { getCountryLocationTranslatedById } = useApiTranslator();
const { getCountryPageTags } = useApiSeo();
const { getTopPropertiesInLocation } = useApiTops();
const { getTopEventsByContinentName } = useApiEvents();
const {
  buildEventDetailsModel,
  getEventPageUrlFromRawEvent,
} = useEventDetailsModel();

// ### STORES
const sessionStore = useSessionStore();
const locationStore = useLocationStore();
const languageStore = useLanguageStore();
const sustainabilityStore = useSustainabilityStore();
const testingStore = useTestingStore();

// ### VARIABLES INITIALIZATION
const urlParams = route?.params || null;
const urlQuery = route?.query || null;
const pageInfoData = {
  numberOfProperties: 0,
  numberOfCities: 0,
  canonicalUrl: '',
  ogImage: '',
  avgRating: '',
  numberOfReviews: '',
};
const locationKey = 'country';
const STAFF_PICKS_LIMIT = 8;
const entryPageName = 'COUNTRY';
const gtmPageName = 'countryLanding';
const gtmClassification = 'country';
const segmentTracking = SEGMENT_PAGE_DATA.country;

// ### ROUTE VALIDATION
const propertyTypeObj = useRouteManager.validatePropertyTypeParam(
  urlParams.propertyType,
);
const propertyTypeKey = propertyTypeObj?.key;

const countryObj = await useRouteManager.validateCountryParam(
  propertyTypeObj,
  urlParams.country,
);
const urlData = useRouteManager.getUrlData(
  urlParams.propertyType,
  urlParams.continent,
  urlParams.country,
);
useRouteManager.redirectRoute(urlData, urlQuery);

const isCountryOgEventsEnabled
  = testingStore.hasEnabledFeature('web_enableOGevents')
    && testingStore.getFeatureVariables('web_enableOGevents')?.country_enabled === true;

const countryEventsPromise = isCountryOgEventsEnabled
  ? getTopEventsByContinentName(
    countryObj?.urlFriendlyContinent,
    STAFF_PICKS_LIMIT,
    countryObj?.urlFriendlyName,
  ).catch(() => ({}))
  : Promise.resolve({});

// ### API
const topPropertiesInLocation = await getTopPropertiesInLocation(
  propertyTypeKey,
  locationKey,
  countryObj?.id,
);
const seoData = await getCountryPageTags(propertyTypeKey, countryObj?.id);
const regionsByCountryData = await getAllRegionsByCountryId(
  propertyTypeKey,
  countryObj?.id,
);
const areasByCountryData = await getAllAreasByCountryId(
  propertyTypeKey,
  countryObj?.id,
);
const urlTranslations = await getCountryLocationTranslatedById(
  countryObj?.continentId,
  countryObj?.id,
);

const hrefUrls = useUrl.generateHrefLangForCountry(
  urlTranslations,
  propertyTypeKey,
);
const averageCityPrices = await getAverageCityPricesByCountryName(
  propertyTypeKey,
  urlParams?.country,
);
const locationEvents = await countryEventsPromise;
const eventsRootSlug = t('t_OGEVENTSSLUG');
const countryStaffPicks = Array.isArray(locationEvents?.events?.staffPicks)
  ? locationEvents.events.staffPicks
  : [];
const countryOgEventsList = countryStaffPicks
  .slice(0, STAFF_PICKS_LIMIT)
  .map((rawEvent) => {
    const enrichedEvent = {
      ...rawEvent,
      location: {
        ...rawEvent?.location,
        cityName: rawEvent?.location?.name || rawEvent?.location?.cityName || '',
        countryName: rawEvent?.location?.countryName || '',
      },
    };
    const pageUrl = getEventPageUrlFromRawEvent(enrichedEvent, eventsRootSlug);

    return buildEventDetailsModel(enrichedEvent, pageUrl);
  })
  .filter((model) => model !== null);
const countryEventsPageUrl = useUrl.getEventsCountryPageUrl({
  rootSlug: eventsRootSlug,
  urlFriendlyContinent: countryObj?.urlFriendlyContinent,
  urlFriendlyCountry: countryObj?.urlFriendlyName,
});

if (countryObj && countryObj?.id) {
  pageInfoData.numberOfProperties = countryObj?.numberOfProperties;
  pageInfoData.numberOfCities = countryObj?.numberOfCities;
  pageInfoData.avgRating = countryObj?.avgRating;
  pageInfoData.numberOfReviews = countryObj?.numberOfReviews;

  const imagesObj = {
    mobile: countryObj?.image?.mobile,
    tablet: countryObj?.image?.tablet,
    desktop: countryObj?.image?.desktop,
    heroImage: countryObj?.image?.heroImage,
  };

  locationStore.setLocation(locationKey, {
    id: countryObj?.id,
    name: countryObj?.name,
    image: imagesObj,
  });

  pageInfoData.canonicalUrl = useUrl.getCountryPageUrl({
    propertyTypeSlug: propertyTypeObj?.slug,
    urlFriendlyContinent: countryObj?.urlFriendlyContinent,
    urlFriendlyCountry: countryObj?.urlFriendlyName,
  });

  pageInfoData.ogImage = useImage.getImage(imagesObj?.desktop);
}

const crumbs = [
  {
    label: t('t_A11YBCTITLEPROPERTYTYPE', { PROPERTYTYPE: t(propertyTypeObj?.transcode) }),
    title: t(propertyTypeObj.transcode),
    link: useUrl.getWorldPageUrl({ propertyTypeSlug: propertyTypeObj?.slug }),
  },
  {
    label: t('t_A11YBCTITLELOCATION', {
      LOCATIONNAME: countryObj.continent,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    title: t('t_A11YBCTEXTLOCATION', {
      LOCATIONNAME: countryObj.continent,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    link: useUrl.getContinentPageUrl({
      propertyTypeSlug: propertyTypeObj?.slug,
      urlFriendlyContinent: countryObj?.urlFriendlyContinent,
    }),
  },
  {
    label: t('t_A11YBCTITLELOCATION', {
      LOCATIONNAME: countryObj.name,
      PROPERTYTYPE: t(propertyTypeObj?.transcode),
    }),
    title: countryObj.name,
    link: useUrl.getCountryPageUrl({
      propertyTypeSlug: propertyTypeObj?.slug,
      urlFriendlyContinent: countryObj?.urlFriendlyContinent,
      urlFriendlyCountry: countryObj?.urlFriendlyName,
    }),
  },
];

const areaType = computed(() => {
  return areasByCountryData && areasByCountryData.areas[0]?.type
    ? areasByCountryData.areas[0]?.type
    : 'province';
});

const areaTitle = computed(() => {
  return t('t_STATESINCOUNTRY', {
    STATES: t(AREAS_TYPES[areaType.value]?.transcode),
    COUNTRY: `<span>${countryObj?.name}</span>`,
  });
});

const regionTitle = computed(() => {
  return t('t_POPULARAREASINCOUNTRY', { COUNTRYNAME: `<span>${countryObj?.name}</span>` });
});

// ### SEO
const tradReplacements = {
  PROPCOUNT: countryObj?.numberOfProperties,
  PROPERTYTYPE: t(propertyTypeObj?.transcode_s),
  PROPERTYTYPES: t(propertyTypeObj?.transcode),
  LOCATIONNAME: countryObj?.name,
  YEAR: new Date().getFullYear(),
  REVIEWCOUNT: countryObj?.numberOfReviews,
  REVIEWSCOUNT: countryObj?.numberOfReviews,
  CONTINENT: countryObj?.continent,
  CHOSENCONTINENT: countryObj?.name,
  CHOSENCOUNTRY: countryObj?.name,
  CHOSENCITY: countryObj?.name,
  COUNTRY: countryObj?.name,
  ACTIVEFILTER: '',
};

const seoMetaData = buildSeo.getMetaData(
  seoData,
  pageInfoData,
  tradReplacements,
  false,
  locationKey,
);

const getRegionsList = computed(() => {
  if (!regionsByCountryData?.regions) {
    return [];
  }

  return regionsByCountryData?.regions?.map((region) => {
    return {
      url: useUrl.getRegionPageUrl({
        propertyTypeSlug: propertyTypeObj?.slug,
        urlFriendlyContinent: region?.urlFriendlyContinent,
        urlFriendlyCountry: region?.urlFriendlyCountry,
        urlFriendlyRegion: region?.urlFriendlyName,
      }),
      title: region.name,
      aria: region.name,
      name: region.name,
    };
  });
});

const getAreasList = computed(() => {
  if (!areasByCountryData?.areas) {
    return [];
  }

  return areasByCountryData?.areas?.map((area) => {
    return {
      url: useUrl.getAreaPageUrl({
        propertyTypeSlug: propertyTypeObj?.slug,
        urlFriendlyContinent: area?.urlFriendlyContinent,
        urlFriendlyCountry: area?.urlFriendlyCountry,
        urlFriendlyArea: area?.urlFriendlyName,
      }),
      title: area.name,
      aria: area.name,
      name: area.name,
    };
  });
});

const getMainTitle = computed(() => {
  if (seoMetaData?.h1) {
    return seoMetaData.h1;
  }

  return t('t_PROPERTYTYPEWITHFILTERINLOCATION', {
    PROPERTYTYPE: t(propertyTypeObj?.transcode),
    LOCATION: countryObj?.name,
    ACTIVEFILTER: '',
  });
});

const hasProperties = computed(() => {
  return pageInfoData?.numberOfProperties > 0;
});

const getPropertiesCountText = computed(() => {
  if (seoMetaData?.h2) {
    return seoMetaData.h2;
  }

  let translation = t('t_RWDWEHAVEXHOSTELSINLOCATIONSHORT', {
    NUMOFPROPERTIES: pageInfoData?.numberOfProperties,
    NUMOFCITIES: pageInfoData?.numberOfCities,
    PROPERTYTYPE:
      pageInfoData?.numberOfProperties === 1
        ? t(propertyTypeObj.transcode_s)
        : t(propertyTypeObj.transcode),
    CITYORCITIES:
      pageInfoData?.numberOfCities === 1 ? t('t_CITY') : t('t_CITIES'),
    LOCATION: `${countryObj?.name}, ${countryObj?.continent}`,
  });

  if (countryObj?.avgRating > 0) {
    translation = `${translation} ${t('t_RWDWEHAVEXHOSTELSINLOCATIONRATINGS', {
      AVGRATING: useFormatter.formatRating(pageInfoData?.avgRating),
      NUMOFREVIEWS: pageInfoData?.numberOfReviews,
    })}`;
  }
  return translation;
});

const getCrumbsShownByViewport = computed(() => {
  return toValue(isExtraSmallScreen) ? 2 : null;
});

const getMapConfigs = computed(() => ({
  scrollZoom: false,
  center: [countryObj.longitude, countryObj?.latitude],
  zoom: 5,
}));

const getMapMakersList = computed(() => {
  const areasOrCities = countryObj?.areas.length
    ? countryObj?.areas
    : countryObj?.cities;

  return areasOrCities
    .filter((location) => location?.totalPropertiesAvailable)
    .reduce((markers, location) => {
      const locationObject = {
        id: location.id,
        name: location.name,
        latitude: location.latitude,
        longitude: location.longitude,
        numProperties: location?.totalPropertiesAvailable || 0,
      };

      if (location?.cities) {
        locationObject.url = useUrl.getAreaPageUrl({
          propertyTypeSlug: propertyTypeObj.slug,
          urlFriendlyContinent: location.urlFriendlyContinent,
          urlFriendlyCountry: location.urlFriendlyCountry,
          urlFriendlyArea: location.urlFriendlyName,
        });
        locationObject.topCities = location.cities.map((city) => ({
          id: city.id,
          name: city.name,
          urlFriendlyName: city.urlFriendlyName,
          urlFriendlyCountry: city.urlFriendlyCountry,
          urlFriendlyContinent: city.urlFriendlyContinent,
        }));
      } else {
        locationObject.url = useUrl.getCityPageUrl({
          propertyTypeSlug: propertyTypeObj?.slug,
          urlFriendlyContinent: location.urlFriendlyContinent,
          urlFriendlyCountry: location.urlFriendlyCountry,
          urlFriendlyCity: location.urlFriendlyName,
        });
        locationObject.citySummary = {
          numProperties: location.totalPropertiesAvailable,
          avgDormPrice: location.avgDormPrice,
          avgPrivatePrice: location.avgPrivatePrice,
        };
      }

      markers.push(locationObject);
      return markers;
    }, []);
});

const hasTopPropertiesInLocation = computed(() => {
  return topPropertiesInLocation && topPropertiesInLocation?.length > 0;
});

const getAppDownloadDetails = computed(() => {
  return {
    page: 'country',
    linkPaths: APPDOWNLOADBANNERLINKS?.COUNTRY,
    linkParams: 'search',
  };
});

function handleSustainabilityPinClick(propertySustainabilityInfo) {
  sustainabilityStore.setSustainabilityDetails(propertySustainabilityInfo);

  const trackingInfo = {
    gtm: {
      parameter1: `SUSTAINABILITY_TIER_${propertySustainabilityInfo?.tier}`,
      parameter2: gtmPageName,
    },
  };

  $tracking.onSustainabilityIconClick(trackingInfo.gtm, trackingInfo.segment);
}

useHead(() => ({
  htmlAttrs: { lang: locale?.value },
  link: [
    {
      rel: 'canonical',
      href: seoMetaData?.canonicalUrl,
    },
  ].concat(hrefUrls),
}));

useSeoMeta({
  title: () => seoMetaData?.title,
  ogTitle: () => seoMetaData?.ogTitle,
  description: () => seoMetaData?.description,
  ogDescription: () => seoMetaData?.ogDescription,
  ogUrl: () => seoMetaData?.canonicalUrl,
  ogImage: () => seoMetaData?.ogImage,
  ogImageAlt: () => seoMetaData?.ogDescription,
  ogType: () => 'website',
  keywords: () => seoMetaData?.keywords,
  robots: () => 'index, follow, maxImagePreview:large',
  twitterCard: () => 'summary_large_image',
  twitterTitle: () => seoMetaData?.ogTitle,
  twitterDescription: () => seoMetaData?.ogDescription,
  twitterImage: () => seoMetaData?.ogImage,
  twitterImageAlt: () => seoMetaData?.ogDescription,
});

const schemaCrumbs = crumbs?.map((item) => {
  return {
    name: item?.title,
    item: item?.link,
  };
});

useSchemaOrg([
  defineBreadcrumb({ itemListElement: schemaCrumbs }),
  {
    '@type': 'Country',
    'name': countryObj.name,
    'description': seoMetaData?.description,
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': countryObj?.latitude,
      'longitude': countryObj?.longitude,
    },
    'image': {
      '@type': 'ImageObject',
      'url': seoMetaData?.ogImage,
    },
    'url': seoMetaData?.canonicalUrl,
  },
]);

// ### TRACKING
sessionStore.setEntryPoint(entryPageName);

onMounted(() => {
  const trackingPropertyType = propertyTypeObj?.type?.toLowerCase();
  const trackingContinentName = countryObj?.tracking?.continent;
  const trackingCountryName = countryObj?.tracking?.name;

  const trackingInfo = {
    gtm: {
      gtmPropertyType: trackingPropertyType,
      gtmCanonicalUrl: seoMetaData?.canonicalUrl,
      gtmContinent: trackingContinentName,
      gtmCountry: trackingCountryName,
      gtmPageName,
      gtmClassification,
    },
    segment: {
      event_name: segmentTracking.VIEWED,
      page_type: segmentTracking.TYPE,
      property_type: trackingPropertyType,
      destination_continent: trackingContinentName,
      destination_country: trackingCountryName,
    },
  };

  if (countryOgEventsList.length) {
    trackingInfo.segment = {
      ...trackingInfo.segment,
      occasion_genius_events: countryOgEventsList
        .map((event) => event.eventId)
        .join('|'),
      occasion_genius_event_country: countryObj?.name,
    };
  }

  $tracking?.trackOnPageLoad(trackingInfo.gtm, trackingInfo.segment);
});
</script>

<script>
/* eslint-disable */
import { ca } from "@/lib/channelAttribution.js";

const entryPageNameJs = "COUNTRY";

if (import.meta.client) {
  ca?.setChannelAttribution(null, entryPageNameJs);
}
/* eslint-enable */
</script>

<template>
  <LazyCommonHwAppDownloadBanner v-bind="getAppDownloadDetails" />

  <CommonHwHeader
    :free-cancellation-text="$t('t_CANCELFORFREEDESCRIPTION')"
    :headline="getMainTitle"
    :sub-label="getPropertiesCountText"
  />

  <WdsBreadCrumbs
    class="hw-wrapper breadcrumbs-section"
    :breadcrumbs="crumbs"
    :max-crumbs-shown="getCrumbsShownByViewport"
    :reversed="false"
  />

  <CommonHwFreeCancellationPropertyTypeContainer
    class="hw-wrapper cancellation-and-property-type-section"
    :active-property-type="propertyTypeObj.key"
  />

  <section
    v-if="!hasProperties"
    class="hw-wrapper middle-section no-results-section"
  >
    <CommonHwNoResults
      :has-results="hasProperties"
      :property-type-obj="propertyTypeObj"
      :location-name="countryObj?.name"
    />
  </section>

  <CommonHwPopularPropertiesInLocation
    v-if="hasTopPropertiesInLocation"
    class="hw-wrapper full-width country-section"
    :property-type-obj="propertyTypeObj"
    :location-name="countryObj?.name"
    :location-list="topPropertiesInLocation"
    @sustainability-pin-click="handleSustainabilityPinClick"
  />

  <EventsHwEventsInLocation
    v-if="countryOgEventsList.length"
    class="hw-wrapper country-section"
    :event-list="countryOgEventsList"
    :city-name="countryObj?.name"
    :view-more-url="countryEventsPageUrl"
  />

  <section
    v-if="hasProperties"
    class="hw-wrapper full-width country-section map-section"
  >
    <CommonHwMap
      class="map-wrapper"
      :map-config="getMapConfigs"
      :marker-list="getMapMakersList"
      :property-type-obj="propertyTypeObj"
    />
  </section>

  <CommonHwAverageCityPrice
    v-if="averageCityPrices"
    class="hw-wrapper country-section"
    :average-city-prices-list="averageCityPrices"
    :property-type-obj="propertyTypeObj"
  />

  <CommonHwOtherLocationsInLocation
    v-if="getAreasList"
    class="hw-wrapper country-section"
    :location-name="countryObj?.name"
    :other-locations="getAreasList"
    :title="areaTitle"
    :property-type-obj="propertyTypeObj"
  />

  <CommonHwOtherLocationsInLocation
    v-if="getRegionsList"
    class="hw-wrapper country-section"
    :location-name="countryObj?.name"
    :other-locations="getRegionsList"
    :title="regionTitle"
    :property-type-obj="propertyTypeObj"
  />

  <CommonHwPageTextContent
    v-if="seoMetaData.copy"
    class="hw-wrapper country-section country-about-section"
    heading-level="h2"
    :title="
      $t('t_ABOUTLOCATION', { LOCATION: `<span>${countryObj?.name}</span>` })
    "
    :content="seoMetaData.copy"
  />

  <CommonHwLanguagePicker v-show="languageStore.isLanguagePickerOpen" />
</template>

<style lang="scss" scoped>
.country-section {
  margin: wds-rem(72px) auto;

  @include desktop {
    margin: wds-rem(120px) auto;
  }
}

.breadcrumbs-section {
  padding-left: $wds-spacing-s;

  @include tablet {
    padding-left: $wds-spacing-m;
  }

  @include desktop {
    padding-left: $wds-spacing-xl;
  }
}

.no-results-section {
  margin: $wds-spacing-xl auto;
}

.cancellation-and-property-type-section {
  padding: 0;

  @include desktop {
    padding: 0 wds-rem(40px);
  }
}

.map-section {
  .map-wrapper,
  :deep(.map-wrapper) {
    height: wds-rem(560px);
  }
}

.country-about-section {
  :deep(.content) {
    p {
      @include body-1-reg;
    }

    a {
      @include body-1-bld;
    }
  }
}
</style>
